import { decodePeriodValue } from '@components'

import type { Organization } from '@modules-deprecated/app/organization/types/organization'

import type { PeriodMode } from '../../../enums/periodMode'
import { REPORT_NAVIGATED_EVENT, REPORT_VIEWED_EVENT } from '../constants/expertReportTrackingEvents'
import type { ExpertReportsAvailability } from '../types/expertReportsAvailability'
import type {
  ExpertReportEntry,
  ExpertReportEntryPoint,
  ReportSession,
  ReportTrackingEvent,
} from '../types/expertReportTracking'
import { closeReportSession } from './closeReportSession'
import { resolveExpertReportEntry } from './resolveExpertReportEntry'

interface GetReportSessionTransitionParams {
  availability: ExpertReportsAvailability
  defaultEncodedPeriod: string
  location: { pathname: string; search: string }
  now: number
  organization: Organization
  previousRouteName: string | undefined
  previousSession: ReportSession | undefined
}

interface ReportSessionTransition {
  events: ReportTrackingEvent[]
  session: ReportSession | undefined
}

// Same resolution as `useExpertReportPeriod`, which only writes its default into the URL after the view mounts.
const getPeriodType = (
  entry: ExpertReportEntry,
  search: string,
  defaultEncodedPeriod: string,
): PeriodMode | undefined => {
  if (!entry.hasPeriod) {
    return undefined
  }

  const encodedPeriod = new URLSearchParams(search).get('period') || defaultEncodedPeriod

  return decodePeriodValue(encodedPeriod)?.mode
}

const getEntryPoint = (previousRouteName: string | undefined, isFromReport: boolean): ExpertReportEntryPoint => {
  if (!previousRouteName) {
    return 'deep_link'
  }

  return isFromReport ? 'cross_report' : 'nav'
}

/**
 * Decides which report events a location or organization change produces, and which report visit is open afterwards.
 * A visit spans one report area under one organization: period changes and drill-ins stay in it, anything else closes
 * it with a Time Spent event before the next one (if any) is opened. A report the organization has no access to counts
 * as being outside the reports, matching `ViewContainer`, which does not render it.
 */
export const getReportSessionTransition = ({
  availability,
  defaultEncodedPeriod,
  location,
  now,
  organization,
  previousRouteName,
  previousSession,
}: GetReportSessionTransitionParams): ReportSessionTransition => {
  const matchedEntry = resolveExpertReportEntry(location.pathname)
  const entry = matchedEntry && availability[matchedEntry.availabilityKey] ? matchedEntry : undefined
  const periodType = entry ? getPeriodType(entry, location.search, defaultEncodedPeriod) : undefined
  const isSameOrganization = previousSession?.organization.id === organization.id

  if (previousSession && isSameOrganization && previousSession.reportType === entry?.reportType) {
    return { events: [], session: { ...previousSession, organization, periodType } }
  }

  const { timeSpentEvent } = closeReportSession(previousSession, now)
  const events: ReportTrackingEvent[] = timeSpentEvent ? [timeSpentEvent] : []

  if (!entry) {
    return { events, session: undefined }
  }

  const isFromReport = !!previousSession && isSameOrganization

  /* eslint-disable @typescript-eslint/naming-convention -- Segment payload keys are snake_case */
  if (previousSession && isFromReport) {
    events.push({
      name: REPORT_NAVIGATED_EVENT,
      organization,
      payload: { from_report_type: previousSession.reportType, to_report_type: entry.reportType },
    })
  }

  events.push({
    name: REPORT_VIEWED_EVENT,
    organization,
    payload: {
      entry_point: getEntryPoint(previousRouteName, isFromReport),
      period_type: periodType,
      report_type: entry.reportType,
      source_view: previousRouteName,
    },
  })
  /* eslint-enable @typescript-eslint/naming-convention */

  return {
    events,
    session: { enteredAt: now, isClosed: false, organization, periodType, reportType: entry.reportType },
  }
}
